// @ts-check
import { Component } from '@theme/component';

/**
 * The floating toast column: transient confirmations and failures, top-right on desktop and across
 * the top on a phone.
 *
 * Markup is not written here. `snippets/custom.toast-host.liquid` ships one <template> per tone,
 * each rendered by the same `custom.alert` snippet the cart banner uses, and this clones one and
 * fills its two lines - so the alert's anatomy has one definition rather than a Liquid copy and a
 * JavaScript copy that drift.
 *
 * Text is written with `textContent`, never as markup: a toast's copy can come from a server error
 * message. A caller that needs the link the design draws passes `linkText` / `linkUrl`.
 */

/** Fires on `document` to raise a toast. Listened for by the host below. */
export const TOAST_EVENT = 'custom:toast';

/**
 * @typedef {object} ToastDetail
 * @property {'success' | 'error'} tone - Which template to clone, and which region announces it.
 * @property {string} [title] - The bold first line.
 * @property {string} [body] - The second line, as plain text.
 * @property {string} [linkText] - Label for a trailing link inside the body.
 * @property {string} [linkUrl] - Where that link goes. Both are needed for either to render.
 */

/** The design's dwell time before a toast takes itself away. */
const VISIBLE_MS = 3000;

/** Beyond this the column starts covering the page it is reporting on, so the oldest goes. */
const MAX_VISIBLE = 3;

/**
 * Raises a toast from anywhere, without importing the host.
 *
 * @param {ToastDetail} detail - What to say and in which tone.
 */
export function showToast(detail) {
  document.dispatchEvent(new CustomEvent(TOAST_EVENT, { detail }));
}

/**
 * @typedef {object} Refs
 * @property {HTMLElement} politeRegion - Announces a success.
 * @property {HTMLElement} assertiveRegion - Announces a failure, which a toast outlives by 3s.
 * @property {HTMLElement} stack - Where the toasts go.
 *
 * @extends {Component<Refs>}
 */
class CustomToastHost extends Component {
  requiredRefs = ['politeRegion', 'assertiveRegion', 'stack'];

  /** @type {WeakMap<HTMLElement, number>} */
  #timers = new WeakMap();

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(TOAST_EVENT, this.#onToast);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(TOAST_EVENT, this.#onToast);
  }

  /** @param {Event} event */
  #onToast = (event) => {
    if (event instanceof CustomEvent && event.detail) this.show(event.detail);
  };

  /**
   * Clones the tone's template, fills it, and starts its clock.
   *
   * @param {ToastDetail} detail - What to say and in which tone.
   */
  show(detail) {
    const template = this.querySelector(`template[data-tone="${CSS.escape(detail.tone)}"]`);
    if (!(template instanceof HTMLTemplateElement)) {
      // A tone with no template would otherwise fail silently and swallow the message entirely.
      console.warn(`custom.toast: no template for tone "${detail.tone}"`, detail);
      return;
    }

    const message = `${detail.title ?? ''} ${detail.body ?? ''}`.trim();
    // An empty coloured box with an X and nothing in it is worse than staying quiet, and it is
    // what a missing copy key produces.
    if (!message) {
      console.warn('custom.toast: nothing to say', detail);
      return;
    }

    const { stack } = this.refs;

    // A repeat of the message already on top reads as one thing happening twice; give the shopper
    // the time back instead. One already leaving is not a match - it is about to be removed.
    const newest = stack.lastElementChild;
    if (
      newest instanceof HTMLElement &&
      !newest.hasAttribute('data-leaving') &&
      newest.dataset.toastMessage === message
    ) {
      this.#startTimer(newest);
      this.#announce(message, detail.tone);
      return;
    }

    const clone = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    const toast = clone.firstElementChild;
    if (!(toast instanceof HTMLElement)) return;

    toast.dataset.toastMessage = message;
    this.#fill(toast, detail);

    this.#raise();
    stack.append(toast);

    while (stack.children.length > MAX_VISIBLE) {
      const evicted = stack.firstElementChild;
      // Stop its clock first: a timer that fires after the element is gone would set `data-leaving`
      // on a detached node and wait for an animationend that can no longer happen.
      if (evicted instanceof HTMLElement) this.#clearTimer(evicted);
      evicted?.remove();
    }

    this.#startTimer(toast);
    this.#announce(message, detail.tone);
  }

  /**
   * Puts the column back on top of the top layer.
   *
   * The cart drawer and quick-add are modal dialogs, and a modal dialog plus its backdrop sit in
   * the top layer, which no z-index can reach - so a toast raised from the drawer (where the
   * discount field lives) would paint behind it. The top layer is ordered by insertion, so a
   * re-show is what keeps this above a dialog that opened after the last toast.
   */
  #raise() {
    const { stack } = this.refs;
    try {
      if (stack.matches(':popover-open')) stack.hidePopover();
      stack.showPopover();
    } catch {
      // No popover support and no polyfill. The z-index fallback still covers every surface that
      // is not a modal dialog, which is most of them.
    }
  }

  /** Drops the column out of the top layer once it has nothing to show. */
  #lower() {
    const { stack } = this.refs;
    if (stack.children.length > 0) return;

    try {
      stack.hidePopover();
    } catch {
      // As #raise: nothing to undo when it was never raised.
    }
  }

  /**
   * Writes the copy into a cloned toast.
   *
   * @param {HTMLElement} toast - The clone.
   * @param {ToastDetail} detail - What to say.
   */
  #fill(toast, detail) {
    const title = toast.querySelector('.custom-alert__title');
    const body = toast.querySelector('.custom-alert__body');

    if (title) title.textContent = detail.title ?? '';
    if (!body) return;

    body.textContent = detail.body ?? '';

    if (!detail.linkText || !detail.linkUrl) return;

    const link = document.createElement('a');
    // Resolved against this origin so a caller cannot land a `javascript:` URL on the page.
    const resolved = URL.parse?.(detail.linkUrl, location.origin);
    if (!resolved || !['http:', 'https:', 'mailto:', 'tel:'].includes(resolved.protocol)) return;

    link.href = resolved.href;
    link.textContent = detail.linkText;
    body.append(' ', link);
  }

  /**
   * (Re)starts one toast's dwell time. Hover and focus hold it, so a shopper reading the message -
   * or tabbing to its X - is not racing a timer they cannot see.
   *
   * @param {HTMLElement} toast - The toast to time.
   */
  #startTimer(toast) {
    this.#clearTimer(toast);
    this.#timers.set(
      toast,
      setTimeout(() => this.#dismiss(toast), VISIBLE_MS)
    );

    // An empty dataset value is falsy, so this guard has to test the attribute: without it every
    // hover adds four more listeners and the count doubles per hover-and-leave.
    if (toast.hasAttribute('data-toast-held')) return;
    toast.setAttribute('data-toast-held', '');
    toast.addEventListener('pointerenter', () => this.#clearTimer(toast));
    toast.addEventListener('focusin', () => this.#clearTimer(toast));
    toast.addEventListener('pointerleave', () => this.#startTimer(toast));
    toast.addEventListener('focusout', () => this.#startTimer(toast));
  }

  /** @param {HTMLElement} toast - The toast whose clock to stop. */
  #clearTimer(toast) {
    const timer = this.#timers.get(toast);
    if (timer != null) clearTimeout(timer);
    this.#timers.delete(toast);
  }

  /**
   * Plays the leaving animation and removes the element when it ends.
   *
   * @param {HTMLElement} toast - The toast to take away.
   */
  #dismiss(toast) {
    this.#clearTimer(toast);
    if (toast.hasAttribute('data-leaving')) return;

    this.#handOffFocus(toast);
    toast.addEventListener(
      'animationend',
      () => {
        toast.remove();
        this.#lower();
      },
      { once: true }
    );
    toast.setAttribute('data-leaving', '');
  }

  /**
   * Moves focus off a toast that is about to go, and only then - its X is a tab stop inside the
   * thing it removes, so removing it with focus on it drops the shopper to `<body>`.
   *
   * @param {HTMLElement} leaving - The toast being removed.
   */
  #handOffFocus(leaving) {
    if (!leaving.contains(document.activeElement)) return;

    const next = [...this.refs.stack.children].find(
      (toast) => toast !== leaving && !toast.hasAttribute('data-leaving')
    );
    const target = next?.querySelector('.custom-alert__close');

    if (target instanceof HTMLElement) target.focus();
    else if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  }

  /**
   * Announces a message. A failure goes to the assertive region: a polite one queues behind
   * whatever is being read, and the toast is gone in three seconds.
   *
   * @param {string} message - What was said.
   * @param {'success' | 'error'} tone - Which region says it.
   */
  #announce(message, tone) {
    const region = tone === 'error' ? this.refs.assertiveRegion : this.refs.politeRegion;

    // Cleared, then written next frame: a screen reader reports a change, and identical text is
    // not one. Appended, not replaced, so two messages raised in one frame stay two.
    const pending = region.dataset.pending ? `${region.dataset.pending} ${message}` : message;
    region.dataset.pending = pending;
    region.textContent = '';

    requestAnimationFrame(() => {
      region.textContent = region.dataset.pending ?? '';
      delete region.dataset.pending;
    });
  }

  /**
   * Takes a toast away early.
   *
   * @param {Event} event - The click on a toast's close button.
   */
  dismiss = (event) => {
    const toast = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('.custom-alert--toast') : null
    );
    if (toast) this.#dismiss(toast);
  };
}

if (!customElements.get('custom-toast-host')) {
  customElements.define('custom-toast-host', CustomToastHost);
}
